
import React from "react"
import { useState, useCallback, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Presentation,
  Sparkles,
  Upload,
  FileText,
  Target,
  Swords,
  BookOpen,
  Loader2,
  Copy,
  Check,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  FileUp,
} from "lucide-react"
import { IreneLogoMark } from "@/components/irene-logo"
import { cn } from "@/lib/utils"
import { useIntercom } from "@/hooks/use-intercom"
import { useCanvaConnection } from "@/lib/use-api"
import { importDeckToCanva } from "@/lib/canva-client"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Dialog, DialogContent } from "@/components/ui/dialog"
const RENDER_SCALE = 1.5

async function parsePdf(
  arrayBuffer: ArrayBuffer,
  onProgress?: (rendered: number, total: number) => void,
): Promise<{ text: string; pages: number; slideImages: SlideImage[] }> {
  const pdfjsLib = await import("pdfjs-dist")
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString()

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pages = pdf.numPages
  const textParts: string[] = []
  const slideImages: SlideImage[] = []

  for (let i = 1; i <= pages; i++) {
    const page = await pdf.getPage(i)

    // Text extraction
    const content = await page.getTextContent()
    const pageText = content.items
      .map((item: any) => item.str)
      .join(" ")
    if (pageText.trim()) {
      textParts.push(`Slide ${i}\n${pageText.trim()}`)
    }

    // Canvas rendering
    try {
      const viewport = page.getViewport({ scale: RENDER_SCALE })
      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext("2d")!

      await page.render({ canvas, canvasContext: ctx, viewport }).promise

      slideImages.push({
        pageNumber: i,
        dataUrl: canvas.toDataURL("image/png"),
        width: viewport.width,
        height: viewport.height,
      })
    } catch {
      // If rendering fails for a page, skip it
    }

    page.cleanup()
    onProgress?.(i, pages)
  }

  pdf.destroy()
  return { text: textParts.join("\n\n"), pages, slideImages }
}

const API_BASE = "https://irene-api.heysalad.app"

function getAuthToken(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|; )irene_token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

interface SlideImage {
  pageNumber: number
  dataUrl: string
  width: number
  height: number
}

interface SlideAnalysis {
  slideNumber: number
  slideTitle: string
  score: number
  feedback: string
}

interface AnalysisResult {
  slides: SlideAnalysis[]
  overall: string
  averageScore: number
  totalSlides: number
}

const analysisTypes = [
  {
    id: "full",
    label: "Full Analysis",
    description: "Comprehensive slide-by-slide review",
    icon: FileText,
  },
  {
    id: "investor-readiness",
    label: "Investor Readiness",
    description: "Score from a VC partner's perspective",
    icon: Target,
  },
  {
    id: "competitive-analysis",
    label: "Competitive Analysis",
    description: "Positioning and competitive advantages",
    icon: Swords,
  },
  {
    id: "storytelling",
    label: "Storytelling Review",
    description: "Narrative flow and persuasion",
    icon: BookOpen,
  },
] as const

function getScoreColor(score: number) {
  if (score >= 8) return "text-emerald-600"
  if (score >= 6) return "text-amber-600"
  return "text-red-500"
}

function getScoreBg(score: number) {
  if (score >= 8) return "bg-emerald-500/10 border-emerald-500/20"
  if (score >= 6) return "bg-amber-500/10 border-amber-500/20"
  return "bg-red-500/10 border-red-500/20"
}

export default function DeckAnalyzerPage() {
  const [deckContent, setDeckContent] = useState("")
  const [additionalContext, setAdditionalContext] = useState("")
  const [selectedType, setSelectedType] = useState<string>("full")
  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analyzeProgress, setAnalyzeProgress] = useState("")
  const [copied, setCopied] = useState(false)
  const [hasAnalyzed, setHasAnalyzed] = useState(false)
  const [inputMode, setInputMode] = useState<"upload" | "paste">("upload")
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const [pageCount, setPageCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [expandedSlides, setExpandedSlides] = useState<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState<"overview" | "slides">("overview")
  const [slideImages, setSlideImages] = useState<SlideImage[]>([])
  const [isRenderingSlides, setIsRenderingSlides] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [lightboxSlide, setLightboxSlide] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { trackDeckAnalyzed } = useIntercom()
  const { data: canvaConnection } = useCanvaConnection()
  const [isImportingToCanva, setIsImportingToCanva] = useState(false)

  const toggleSlide = (slideNum: number) => {
    setExpandedSlides((prev) => {
      const next = new Set(prev)
      if (next.has(slideNum)) next.delete(slideNum)
      else next.add(slideNum)
      return next
    })
  }

  const expandAll = () => {
    if (result) {
      setExpandedSlides(new Set(result.slides.map((s) => s.slideNumber)))
    }
  }

  const collapseAll = () => setExpandedSlides(new Set())

  const handleFileUpload = useCallback(async (file: File) => {
    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file.")
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      alert("File must be under 20MB.")
      return
    }

    setUploadedFile(file)
    setIsParsing(true)
    setIsRenderingSlides(true)
    setRenderProgress(0)
    setSlideImages([])

    try {
      const arrayBuffer = await file.arrayBuffer()
      const result = await parsePdf(arrayBuffer, (rendered) => {
        setRenderProgress(rendered)
      })
      setDeckContent(result.text)
      setPageCount(result.pages)
      setSlideImages(result.slideImages)
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to parse PDF. Try pasting the text instead.")
      setUploadedFile(null)
      setSlideImages([])
    } finally {
      setIsParsing(false)
      setIsRenderingSlides(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFileUpload(file)
    },
    [handleFileUpload],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileUpload(file)
    },
    [handleFileUpload],
  )

  const handleAnalyze = useCallback(async () => {
    if (!deckContent.trim()) return
    setIsAnalyzing(true)
    setResult(null)
    setHasAnalyzed(true)
    setAnalyzeProgress("Splitting deck into slides...")

    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error("You must be logged in to analyze decks. Please sign in and try again.")
      }

      const progressTimer = setTimeout(() => setAnalyzeProgress("Analyzing each slide with AI..."), 3000)
      const progressTimer2 = setTimeout(() => setAnalyzeProgress("Generating overall assessment..."), 15000)
      const progressTimer3 = setTimeout(() => setAnalyzeProgress("Almost done, preparing results..."), 25000)

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      }

      const response = await fetch(`${API_BASE}/api/analyze-deck`, {
        method: "POST",
        headers,
        mode: "cors",
        body: JSON.stringify({
          deckContent: deckContent.trim(),
          analysisType: selectedType,
          context: additionalContext.trim() || undefined,
        }),
      })

      clearTimeout(progressTimer)
      clearTimeout(progressTimer2)
      clearTimeout(progressTimer3)

      const json = await response.json().catch(() => null) as any

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Session expired. Please log in again.")
        }
        if (response.status === 429) {
          throw new Error(json?.error || "Rate limit exceeded. Please wait a moment and try again.")
        }
        if (response.status === 403) {
          throw new Error(json?.error || "Your plan does not include deck analysis. Please upgrade.")
        }
        throw new Error(json?.error || json?.message || `Analysis failed (${response.status})`)
      }

      if (!json) {
        throw new Error("Empty response from server. Please try again.")
      }

      // Backend wraps response as { success, data: { slides, overall, ... } }
      const data = json.data || json

      if (!data.slides || !Array.isArray(data.slides)) {
        throw new Error("Invalid analysis response. Please try again.")
      }

      setResult(data)
      setActiveTab("overview")
      // Expand first slide by default
      if (data.slides.length > 0) {
        setExpandedSlides(new Set([1]))
      }

      try {
        trackDeckAnalyzed({
          analysisType: selectedType,
          charCount: deckContent.length,
        })
      } catch {
        // non-critical
      }
    } catch (err) {
      setResult(null)
      setAnalyzeProgress(err instanceof Error ? err.message : "Analysis failed. Please try again.")
      setIsAnalyzing(false)
      return
    }
    setIsAnalyzing(false)
  }, [deckContent, selectedType, additionalContext, trackDeckAnalyzed])

  const handleCopy = useCallback(() => {
    if (!result) return
    const fullText = `OVERALL ANALYSIS\n\n${result.overall}\n\n---\n\nSLIDE-BY-SLIDE ANALYSIS\n\n${result.slides.map((s) => `SLIDE ${s.slideNumber}: ${s.slideTitle} (${s.score}/10)\n\n${s.feedback}`).join("\n\n---\n\n")}`
    navigator.clipboard.writeText(fullText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [result])

  const handleReset = useCallback(() => {
    setDeckContent("")
    setAdditionalContext("")
    setResult(null)
    setHasAnalyzed(false)
    setSelectedType("full")
    setUploadedFile(null)
    setPageCount(0)
    setInputMode("upload")
    setExpandedSlides(new Set())
    setAnalyzeProgress("")
    setSlideImages([])
    setRenderProgress(0)
    setActiveSlideIndex(0)
    setLightboxSlide(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }, [])

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null)
    setDeckContent("")
    setPageCount(0)
    setSlideImages([])
    setRenderProgress(0)
    setActiveSlideIndex(0)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }, [])

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-foreground">
            <Presentation className="h-7 w-7 text-primary" />
            Deck Analyzer
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload your pitch deck and get slide-by-slide AI feedback from Irene.
          </p>
        </div>
        {hasAnalyzed && (
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            Start Over
          </Button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Input */}
        <div className="flex flex-col gap-5">
          {/* Upload / Paste toggle */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-primary" />
                  Your Deck
                </span>
                <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                  <button
                    type="button"
                    onClick={() => setInputMode("upload")}
                    className={cn(
                      "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                      inputMode === "upload" ? "bg-foreground text-card" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Upload PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setInputMode("paste")}
                    className={cn(
                      "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                      inputMode === "paste" ? "bg-foreground text-card" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Paste text
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inputMode === "upload" ? (
                <>
                  {!uploadedFile ? (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        "flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed transition-colors",
                        isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted/30",
                      )}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click()
                      }}
                    >
                      <div className={cn("flex h-14 w-14 items-center justify-center rounded-2xl transition-colors", isDragging ? "bg-primary/10" : "bg-muted")}>
                        <FileUp className={cn("h-7 w-7", isDragging ? "text-primary" : "text-muted-foreground")} />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-foreground">{isDragging ? "Drop your PDF here" : "Drag and drop your deck"}</p>
                        <p className="mt-1 text-xs text-muted-foreground">or click to browse. PDF up to 20MB.</p>
                      </div>
                      <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleFileInputChange} className="sr-only" aria-label="Upload pitch deck PDF" />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{uploadedFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {pageCount > 0 && `${pageCount} pages`}
                            {pageCount > 0 && " \u00B7 "}
                            {(uploadedFile.size / 1024).toFixed(0)} KB
                            {deckContent && ` \u00B7 ${deckContent.length.toLocaleString()} chars`}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleRemoveFile} className="shrink-0">
                          <X className="h-4 w-4" />
                          <span className="sr-only">Remove file</span>
                        </Button>
                      </div>
                      {(isParsing || isRenderingSlides) && (
                        <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {isParsing
                            ? "Extracting text from PDF..."
                            : `Rendering slides (${renderProgress}/${pageCount})...`}
                        </div>
                      )}
                      {slideImages.length > 0 && (
                        <SlidePreviewStrip
                          images={slideImages}
                          isRendering={isRenderingSlides}
                          renderProgress={renderProgress}
                          totalPages={pageCount}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Textarea
                    placeholder={"Paste your pitch deck content here, slide by slide...\n\nSlide 1 - Title\nYour Company Name\nTagline\n\nSlide 2 - Problem\n- Problem point 1\n- Problem point 2"}
                    value={deckContent}
                    onChange={(e) => setDeckContent(e.target.value)}
                    className="min-h-[220px] resize-none text-sm leading-relaxed"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {deckContent.length > 0 ? `${deckContent.length.toLocaleString()} chars` : 'Tip: Separate slides with "Slide 1", "Slide 2" or triple newlines'}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Additional Context */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <IreneLogoMark size={18} />
                Context for Irene
                <Badge variant="secondary" className="text-xs font-normal">
                  Optional
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Tell Irene anything: your stage, who you're pitching to, specific concerns..."
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                className="min-h-[80px] resize-none text-sm"
              />
            </CardContent>
          </Card>

          {/* Analysis Type */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-primary" />
                Analysis Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {analysisTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedType(type.id)}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 text-left transition-all",
                      selectedType === type.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30 hover:bg-muted/50",
                    )}
                  >
                    <type.icon className={cn("mt-0.5 h-4 w-4 shrink-0", selectedType === type.id ? "text-primary" : "text-muted-foreground")} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{type.label}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Analyze Button */}
          <Button size="lg" onClick={handleAnalyze} disabled={!deckContent.trim() || isAnalyzing} className="w-full">
            {isAnalyzing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {analyzeProgress || "Analyzing..."}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Analyze Deck
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Right: Results */}
        <div className="flex flex-col gap-4">
          {!hasAnalyzed ? (
            <Card className="flex min-h-[600px] items-center justify-center">
              <div className="flex flex-col items-center gap-4 p-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                  <IreneLogoMark size={32} />
                </div>
                <div>
                  <h3 className="font-sans text-lg font-semibold text-foreground">Ready to analyze</h3>
                  <p className="mt-1 max-w-sm text-sm leading-relaxed text-muted-foreground">
                    Upload your pitch deck and Irene will analyze it slide by slide with scores, strengths, and fixes.
                  </p>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  {["Per-slide scores", "Strengths", "Fixes", "Investor Qs", "Overall verdict"].map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </Card>
          ) : isAnalyzing ? (
            <Card className="flex min-h-[600px] items-center justify-center">
              <div className="flex flex-col items-center gap-4 p-8 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <div>
                  <h3 className="font-sans text-lg font-semibold text-foreground">Analyzing your deck</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{analyzeProgress}</p>
                  <p className="mt-3 text-xs text-muted-foreground">This can take 20-40 seconds for a full deck.</p>
                </div>
              </div>
            </Card>
          ) : result ? (
            <>
              {/* Score header */}
              <Card>
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn("flex h-16 w-16 items-center justify-center rounded-2xl border", getScoreBg(result.averageScore))}>
                        <span className={cn("text-2xl font-bold", getScoreColor(result.averageScore))}>{result.averageScore}</span>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">Average Score</p>
                        <p className="text-sm text-muted-foreground">
                          {result.totalSlides} slides analyzed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canvaConnection?.connected && uploadedFile && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isImportingToCanva}
                          onClick={async () => {
                            // This is a placeholder — a real implementation would
                            // first upload the file via /deck-analyzer/upload, get
                            // back a deckId, then call importDeckToCanva(deckId).
                            // For now we show the button as a UI integration point.
                            setIsImportingToCanva(true)
                            try {
                              // TODO: wire up with actual deckId from upload response
                              alert("To use this feature, upload your deck via the API first, then import to Canva.")
                            } finally {
                              setIsImportingToCanva(false)
                            }
                          }}
                        >
                          {isImportingToCanva ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Presentation className="mr-1.5 h-3.5 w-3.5" />
                          )}
                          Open in Canva
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={handleCopy}>
                        {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                        {copied ? "Copied" : "Copy all"}
                      </Button>
                    </div>
                  </div>

                  {/* Mini slide score bar */}
                  <div className="mt-4 flex items-end gap-1">
                    {result.slides.map((slide) => (
                      <button
                        key={slide.slideNumber}
                        type="button"
                        onClick={() => {
                          setActiveTab("slides")
                          const imageIndex = slideImages.findIndex((img) => img.pageNumber === slide.slideNumber)
                          if (imageIndex >= 0) setActiveSlideIndex(imageIndex)
                          setExpandedSlides((prev) => new Set([...prev, slide.slideNumber]))
                        }}
                        className="group flex flex-1 flex-col items-center gap-1"
                        title={`Slide ${slide.slideNumber}: ${slide.slideTitle} (${slide.score}/10)`}
                      >
                        <div
                          className={cn(
                            "w-full rounded-sm transition-all group-hover:opacity-80",
                            slide.score >= 8 ? "bg-emerald-500" : slide.score >= 6 ? "bg-amber-500" : "bg-red-400",
                          )}
                          style={{ height: `${Math.max(slide.score * 4, 8)}px` }}
                        />
                        <span className="text-[10px] text-muted-foreground">{slide.slideNumber}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tab toggle */}
              <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                <button
                  type="button"
                  onClick={() => setActiveTab("overview")}
                  className={cn("flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors", activeTab === "overview" ? "bg-foreground text-card" : "text-muted-foreground hover:text-foreground")}
                >
                  Overall Analysis
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("slides")}
                  className={cn("flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors", activeTab === "slides" ? "bg-foreground text-card" : "text-muted-foreground hover:text-foreground")}
                >
                  Slide by Slide ({result.totalSlides})
                </button>
              </div>

              {/* Tab content */}
              {activeTab === "overview" ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <IreneLogoMark size={18} />
                      Overall Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none text-foreground prose-headings:font-sans prose-headings:text-foreground prose-strong:text-foreground prose-h2:mt-6 prose-h2:mb-2 prose-h2:text-lg prose-h3:mt-4 prose-h3:mb-1 prose-h3:text-base prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:leading-relaxed prose-li:text-muted-foreground">
                      <MarkdownRenderer content={result.overall} />
                    </div>
                  </CardContent>
                </Card>
              ) : slideImages.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {/* Visual slide viewer */}
                  <Card>
                    <CardContent className="py-4">
                      <SlideViewer
                        images={slideImages}
                        activeIndex={activeSlideIndex}
                        onChangeIndex={setActiveSlideIndex}
                        onClickZoom={(idx) => setLightboxSlide(idx)}
                      />
                    </CardContent>
                  </Card>

                  {/* Feedback for current slide */}
                  {(() => {
                    const currentPageNum = slideImages[activeSlideIndex]?.pageNumber ?? activeSlideIndex + 1
                    const slideAnalysis = result.slides.find((s) => s.slideNumber === currentPageNum)
                    if (!slideAnalysis) {
                      return (
                        <Card>
                          <CardContent className="py-8 text-center text-sm text-muted-foreground">
                            No analysis available for this slide.
                          </CardContent>
                        </Card>
                      )
                    }

                    return (
                      <Card>
                        <CardContent className="pt-5">
                          <div className="mb-4 flex items-center gap-3">
                            <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border text-base font-bold", getScoreBg(slideAnalysis.score))}>
                              <span className={getScoreColor(slideAnalysis.score)}>{slideAnalysis.score}</span>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">Slide {slideAnalysis.slideNumber}</p>
                              <p className="text-xs text-muted-foreground">{slideAnalysis.slideTitle}</p>
                            </div>
                          </div>
                          <div className="prose prose-sm max-w-none text-foreground prose-headings:font-sans prose-headings:text-foreground prose-strong:text-foreground prose-h2:mt-4 prose-h2:mb-2 prose-h2:text-base prose-h3:mt-3 prose-h3:mb-1 prose-h3:text-sm prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:leading-relaxed prose-li:text-muted-foreground">
                            <MarkdownRenderer content={slideAnalysis.feedback} />
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })()}

                  {/* Prev / Next navigation */}
                  <div className="flex items-center justify-between">
                    <Button variant="outline" size="sm" disabled={activeSlideIndex === 0} onClick={() => setActiveSlideIndex((i) => i - 1)}>
                      <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {activeSlideIndex + 1} of {slideImages.length}
                    </span>
                    <Button variant="outline" size="sm" disabled={activeSlideIndex >= slideImages.length - 1} onClick={() => setActiveSlideIndex((i) => i + 1)}>
                      Next
                      <ChevronRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                /* Fallback: collapsible text list for paste mode */
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={expandAll} className="text-xs text-muted-foreground hover:text-foreground">
                      Expand all
                    </button>
                    <span className="text-xs text-border">|</span>
                    <button type="button" onClick={collapseAll} className="text-xs text-muted-foreground hover:text-foreground">
                      Collapse all
                    </button>
                  </div>

                  {result.slides.map((slide) => (
                    <Card key={slide.slideNumber} className="overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSlide(slide.slideNumber)}
                        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/30"
                      >
                        <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-sm font-bold", getScoreBg(slide.score))}>
                          <span className={getScoreColor(slide.score)}>{slide.score}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">
                            Slide {slide.slideNumber}
                            <span className="ml-2 font-normal text-muted-foreground">{slide.slideTitle}</span>
                          </p>
                        </div>
                        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", expandedSlides.has(slide.slideNumber) && "rotate-180")} />
                      </button>

                      {expandedSlides.has(slide.slideNumber) && (
                        <CardContent className="border-t border-border pt-4">
                          <div className="prose prose-sm max-w-none text-foreground prose-headings:font-sans prose-headings:text-foreground prose-strong:text-foreground prose-h2:mt-4 prose-h2:mb-2 prose-h2:text-base prose-h3:mt-3 prose-h3:mb-1 prose-h3:text-sm prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:leading-relaxed prose-li:text-muted-foreground">
                            <MarkdownRenderer content={slide.feedback} />
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Card className="flex min-h-[600px] items-center justify-center">
              <div className="flex flex-col items-center gap-4 p-8 text-center">
                <p className="text-sm text-muted-foreground">{analyzeProgress}</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <SlideLightbox
        images={slideImages}
        activeIndex={lightboxSlide}
        onClose={() => setLightboxSlide(null)}
        onChangeIndex={setLightboxSlide}
      />
    </div>
  )
}

function MarkdownRenderer({ content }: { content: string }) {
  const lines = content.split("\n")
  const elements: React.ReactNode[] = []
  let listItems: string[] = []
  let listType: "ul" | "ol" | null = null

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const Tag = listType
      elements.push(
        <Tag key={`list-${elements.length}`}>
          {listItems.map((item, i) => (
            <li key={i}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </Tag>,
      )
      listItems = []
      listType = null
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (line.startsWith("## ")) {
      flushList()
      elements.push(
        <h2 key={i}>
          <InlineMarkdown text={line.slice(3)} />
        </h2>,
      )
    } else if (line.startsWith("### ")) {
      flushList()
      elements.push(
        <h3 key={i}>
          <InlineMarkdown text={line.slice(4)} />
        </h3>,
      )
    } else if (line.startsWith("# ")) {
      flushList()
      elements.push(
        <h2 key={i}>
          <InlineMarkdown text={line.slice(2)} />
        </h2>,
      )
    } else if (line.match(/^[-*]\s/)) {
      if (listType !== "ul") flushList()
      listType = "ul"
      listItems.push(line.replace(/^[-*]\s/, ""))
    } else if (line.match(/^\d+\.\s/)) {
      if (listType !== "ol") flushList()
      listType = "ol"
      listItems.push(line.replace(/^\d+\.\s/, ""))
    } else if (line.trim() === "") {
      flushList()
    } else {
      flushList()
      elements.push(
        <p key={i}>
          <InlineMarkdown text={line} />
        </p>,
      )
    }
  }
  flushList()

  return <>{elements}</>
}

function InlineMarkdown({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function SlidePreviewStrip({
  images,
  isRendering,
  renderProgress,
  totalPages,
}: {
  images: SlideImage[]
  isRendering: boolean
  renderProgress: number
  totalPages: number
}) {
  return (
    <div className="flex flex-col gap-3">
      {isRendering && images.length < totalPages && (
        <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Rendering slide {renderProgress} of {totalPages}...
        </div>
      )}
      {images.length > 0 && (
        <ScrollArea className="w-full">
          <div className="flex gap-2 pb-2">
            {images.map((img) => (
              <div
                key={img.pageNumber}
                className="relative shrink-0 overflow-hidden rounded-lg border border-border bg-white shadow-sm"
                style={{ width: 120 }}
              >
                <img
                  src={img.dataUrl}
                  alt={`Slide ${img.pageNumber}`}
                  className="h-auto w-full"
                  loading="lazy"
                />
                <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  {img.pageNumber}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}

function SlideViewer({
  images,
  activeIndex,
  onChangeIndex,
  onClickZoom,
}: {
  images: SlideImage[]
  activeIndex: number
  onChangeIndex: (index: number) => void
  onClickZoom: (index: number) => void
}) {
  const current = images[activeIndex]
  if (!current) return null

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative cursor-pointer overflow-hidden rounded-xl border border-border bg-white shadow-sm"
        onClick={() => onClickZoom(activeIndex)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") onClickZoom(activeIndex)
        }}
      >
        <img
          src={current.dataUrl}
          alt={`Slide ${current.pageNumber}`}
          className="h-auto w-full"
        />
        <div className="absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">
          {current.pageNumber} / {images.length}
        </div>
      </div>

      <ScrollArea className="w-full">
        <div className="flex gap-1.5 pb-1">
          {images.map((img, idx) => (
            <button
              key={img.pageNumber}
              type="button"
              onClick={() => onChangeIndex(idx)}
              className={cn(
                "shrink-0 overflow-hidden rounded-md border-2 transition-all",
                idx === activeIndex
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-transparent opacity-60 hover:opacity-100",
              )}
              style={{ width: 64 }}
            >
              <img
                src={img.dataUrl}
                alt={`Slide ${img.pageNumber}`}
                className="h-auto w-full"
              />
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

function SlideLightbox({
  images,
  activeIndex,
  onClose,
  onChangeIndex,
}: {
  images: SlideImage[]
  activeIndex: number | null
  onClose: () => void
  onChangeIndex: (index: number) => void
}) {
  if (activeIndex === null) return null
  const current = images[activeIndex]
  if (!current) return null

  return (
    <Dialog open={activeIndex !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl p-2">
        <div className="relative">
          <img
            src={current.dataUrl}
            alt={`Slide ${current.pageNumber}`}
            className="h-auto w-full rounded-lg"
          />
          {activeIndex > 0 && (
            <button
              type="button"
              onClick={() => onChangeIndex(activeIndex - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {activeIndex < images.length - 1 && (
            <button
              type="button"
              onClick={() => onChangeIndex(activeIndex + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm font-medium text-white">
            Slide {current.pageNumber} of {images.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
